import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi } from "../../utils/api";
import "./BilingualInvoicePrint.scss";

export type InvoiceKind = "order" | "shipForMe";

interface InvoicePrintData {
  number: string;
  date: string;
  currency: string;
  name?: string;
  address?: string;
  address2?: string;
  phone?: string;
  status: number;
  description?: string;
  itemPrice: string | number;
  quantity?: number;
  orderPrice: string | number;
  servicePrice: string | number;
  discount: string | number;
  extraService: string | number;
  merchentDeliveryFee?: string | number;
  totalPrice: string | number;
  invoiceAmount: string | number;
  totalPaid: string | number;
  netDue: string | number;
  remaining: string | number;
  orderId?: string;
  shipForMeId?: string;
  paymentGateway?: string;
}

interface BilingualInvoicePrintProps {
  kind: InvoiceKind;
}

const Label: React.FC<{ en: string; ar: string }> = ({ en, ar }) => (
  <span className="bilingual-label">
    <span dir="ltr">{en}</span>/<span dir="rtl">{ar}</span>
  </span>
);

const formatMoney = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === "") return "0";
  const numericValue = typeof value === "number"
    ? value
    : Number(value.toString().replace(/,/g, ""));

  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : value.toString();
};

const getStatus = (status: number) => {
  switch (status) {
    case 0:
      return { en: "Paid", ar: "مدفوع", color: "#28a745", background: "rgba(40, 167, 69, .03)" };
    case 2:
      return { en: "Cancelled", ar: "ملغي", color: "#e0a800", background: "rgba(255, 193, 7, .04)" };
    case 3:
      return { en: "Refunded", ar: "مسترجع", color: "#fd7e14", background: "rgba(253, 126, 20, .04)" };
    default:
      return { en: "Unpaid", ar: "غير مدفوع", color: "#e52e43", background: "rgba(229, 46, 67, .03)" };
  }
};

// Same "yyyy-MM-dd" pattern the backend uses for both invoice.date (header) and the
// PDF's print date, so the header and footer dates render in the same format here too.
const formatDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const printDate = (): string => formatDate(new Date());

const SummaryRow: React.FC<{
  en: string;
  ar: string;
  value: string | number | undefined;
  className?: string;
}> = ({ en, ar, value, className }) => (
  <tr className={className}>
    <td className="amount">{formatMoney(value)}</td>
    <td className="label"><Label en={en} ar={ar} /></td>
  </tr>
);

// Mirrors InvoiceHtmlTemplate.FormatPaymentMethod on the backend: PaymentGateway.Name is
// often stored as a bilingual JSON object ({"en": "...", "ar": "..."}), so parse that instead
// of dumping raw JSON onto the printed invoice. Falls back to the raw string for anything else.
const PaymentMethodValue: React.FC<{ paymentGateway?: string }> = ({ paymentGateway }) => {
  const value = paymentGateway?.trim();
  if (!value) return <>-</>;

  if (value.startsWith("{")) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const en = typeof parsed.en === "string" ? parsed.en : undefined;
        const ar = typeof parsed.ar === "string" ? parsed.ar : undefined;

        if (ar && en && ar.trim().toLowerCase() === en.trim().toLowerCase()) {
          return <span dir="ltr">{en}</span>;
        }
        if (ar && en) {
          return (
            <span className="bilingual-label" dir="rtl">
              <span dir="rtl">{ar}</span>/<span dir="ltr">{en}</span>
            </span>
          );
        }
        const single = ar || en;
        if (single) return <span dir="auto">{single}</span>;
      }
    } catch {
      // Not valid JSON - fall through and show the raw value below.
    }
  }

  return <span dir="auto">{value}</span>;
};

const BilingualInvoicePrint: React.FC<BilingualInvoicePrintProps> = ({ kind }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [invoice, setInvoice] = useState<InvoicePrintData | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "invoices"]);
  const handlePrint = useReactToPrint({ contentRef });

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      const endpoint = kind === "order"
        ? `/adminorder/printinvoice/${id}`
        : `/adminshipforme/printinvoice/${id}`;
      const response = await adminApi.call<InvoicePrintData>(endpoint, "GET");

      if (response.success && response.data) {
        setInvoice(response.data);
      } else {
        console.error("Failed to fetch invoice:", response.error);
      }
      setLoading(false);
    };

    fetchInvoice();
  }, [id, kind]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status" />
        <p className="mt-2">{t("common:loading")}</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center p-4">
        <div className="alert alert-warning">{t("common:error")}</div>
      </div>
    );
  }

  const status = getStatus(Number(invoice.status));
  const referenceId = kind === "order" ? invoice.orderId : invoice.shipForMeId;

  return (
    <div className="invoice-print-screen">
      <div className="invoice-print-actions no-print">
        <button className="btn btn-outline-primary m-1" onClick={handlePrint}>
          <i className="fa fa-print" /> {t("invoices:print.printButton")}
        </button>
        <button className="btn btn-light m-1" onClick={() => navigate(-1)}>
          <i className="fa fa-arrow-left" /> {t("common:back")}
        </button>
      </div>

      <div ref={contentRef} className="bilingual-invoice" dir="ltr">
        <header className="invoice-header">
          <img className="invoice-logo" src="/logo-com.png" alt="9AR / صار" />
          <div
            className="status-stamp"
            style={{ color: status.color, borderColor: status.color, backgroundColor: status.background }}
          >
            <Label en={status.en} ar={status.ar} />
          </div>

          <div className="invoice-meta">
            <div className="meta-row"><span><Label en="Date" ar="التاريخ" />:</span><span dir="ltr">{invoice.date}</span></div>
            <div className="meta-row"><span><Label en="Invoice No." ar="رقم الفاتورة" />:</span><span dir="ltr">{invoice.number}</span></div>
            <div className="meta-row"><span><Label en="Order No." ar="رقم الطلب" />:</span><span dir="ltr">{referenceId || "-"}</span></div>
          </div>

          <section className="consignee">
            <div className="section-title"><Label en="Consignee" ar="المرسل إليه" /></div>
            <div className="consignee-details" dir="rtl">
              <strong dir="auto">{invoice.name || "-"}</strong>
              <div dir="auto">{invoice.address || "-"}</div>
              <div dir="auto">{invoice.address2 || "-"}</div>
              <div className="meta-row"><span>الهاتف:</span><span dir="ltr">{invoice.phone || "-"}</span></div>
            </div>
          </section>
        </header>

        <table className="items-table" dir="rtl">
          <thead>
            <tr>
              <th className="description"><Label en="Items Description" ar="الوصف" /></th>
              <th className="quantity"><Label en="Qty." ar="الكمية" /></th>
              <th className="unit-price"><Label en="Unit Price" ar="سعر القطعة" /></th>
              <th className="total-price"><Label en="Total Price" ar="السعر الإجمالي" /></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="description" dir="auto">{invoice.description || "-"}</td>
              <td className="quantity">{invoice.quantity || 0}</td>
              <td className="unit-price">{formatMoney(invoice.itemPrice)}</td>
              <td className="total-price">{formatMoney(invoice.orderPrice)}</td>
            </tr>
          </tbody>
        </table>

        <section className="invoice-details">
          <div className="summary">
            <table><tbody>
              <SummaryRow en="Sub-Total" ar="المجموع الفرعي" value={invoice.orderPrice} />
              <SummaryRow en="Merchant Delivery" ar="توصيل التاجر" value={invoice.merchentDeliveryFee} />
              <SummaryRow en="Service Price" ar="رسوم الخدمة" value={invoice.servicePrice} />
              <SummaryRow en="Discount" ar="الخصم" value={invoice.discount} />
              <SummaryRow en="Extra Services" ar="خدمات إضافية" value={invoice.extraService} />
              <SummaryRow en="Total Price" ar="السعر الإجمالي" value={invoice.totalPrice} className="total-row" />
              <SummaryRow en="Invoice Total" ar="إجمالي الفاتورة" value={invoice.invoiceAmount} />
              <SummaryRow en="Paid Amount" ar="المبلغ المدفوع" value={invoice.totalPaid} />
              <SummaryRow en="Remaining Amount" ar="المتبقي" value={invoice.remaining} />
              {/* <SummaryRow en="Due Amount" ar="المبلغ المستحق" value={invoice.netDue} className="due-row" /> */}
            </tbody></table>
          </div>

          <div className="other-info">
            <div className="section-title"><Label en="Other info" ar="معلومات إضافية" /></div>
            <div className="other-info-content">
              <strong><Label en="Extra Services" ar="خدمات إضافية" />:</strong> -
              <div className="payment-method-line">
                <Label en="Payment Method" ar="طريقة الدفع" />: <PaymentMethodValue paymentGateway={invoice.paymentGateway} />
              </div>
            </div>
          </div>
        </section>

        <section className="contact-block">
          <div dir="rtl">إذا كان لديك أي أسئلة حول هذه الفاتورة، يرجى الاتصال بنا:</div>
          <div>If you have any inquiry about the invoice, please contact us:</div>
          <div className="contact-info">info@9ar.store ,7866601010</div>
          <div className="thanks-ar" dir="rtl">شكراً لتعاملكم معنا!</div>
          <div className="thanks-en">Thanks for your business with us</div>
        </section>

        <footer className="page-footer">
          <div className="print-date"><Label en="Print Date" ar="تاريخ الطباعة" />: {printDate()}</div>
          <div className="addresses">
            <div>9arstore, Building 105, 101H St., Baghdad, Iraq</div>
            <div className="address-ar">متجر صار، بناية ١٠٥، شارع ١٠١، بغداد، العراق</div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BilingualInvoicePrint;
